import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
import _objectSpread from "@babel/runtime/helpers/esm/objectSpread2";
import { createAction } from 'redux-actions';
import { CALL_SERVICE } from '@instructure/redux-service-middleware';
import { getUsers, getPageLoading, getPageNumber, getResults, getHighestPageSeen, getPageCount, getRollups, getLoadingRemainingPages } from "./selectors.js";
import { SET_REPORT_PAGE, SET_REPORT_PAGE_DATA, SET_REPORT_OUTCOMES, SET_REPORT_ROLLUPS, SET_REPORT_RESULTS, SET_REPORT_USERS, SET_REPORT_LOADING, SET_REMAINING_PAGES_LOADING, CLEAR_REPORT_DATA, VIEW_REPORT_ALIGNMENT, CLOSE_REPORT_ALIGNMENT, ALL_USERS, ERROR, IN_PROGRESS, COMPLETED } from "../../constants.js";
import { getConfig } from "../config/selectors.js";
import { setError } from "../context/actions.js";
/*
 * action creators
 */
export var setReportOutcomes = createAction(SET_REPORT_OUTCOMES);
export var setRollups = createAction(SET_REPORT_ROLLUPS);
export var setResults = createAction(SET_REPORT_RESULTS);
export var setUsers = createAction(SET_REPORT_USERS);
export var setPageData = createAction(SET_REPORT_PAGE_DATA);
export var setPage = createAction(SET_REPORT_PAGE);
export var setLoading = createAction(SET_REPORT_LOADING);
export var setLoadingRemainingPages = createAction(SET_REMAINING_PAGES_LOADING);
export var clearReportData = createAction(CLEAR_REPORT_DATA);
export var viewReportAlignment = createAction(VIEW_REPORT_ALIGNMENT);
export var closeReportAlignment = createAction(CLOSE_REPORT_ALIGNMENT);

// Function calls to Outcome Service
export var loadUsers = function loadUsers(artifactType, artifactId, pageNumber) {
  return function (dispatch, getState, _arg, scope) {
    var _getConfig = getConfig(getState(), scope),
      host = _getConfig.host,
      jwt = _getConfig.jwt;
    return dispatch({
      type: CALL_SERVICE,
      payload: {
        service: 'outcomes',
        method: 'getUsers',
        args: [host, jwt, artifactType, artifactId, pageNumber]
      }
    });
  };
};
var getOutcomeResults = function getOutcomeResults(artifactType, artifactId, outcomeId, userList) {
  return function (dispatch, getState, _arg, scope) {
    var _getConfig2 = getConfig(getState(), scope),
      host = _getConfig2.host,
      jwt = _getConfig2.jwt;
    return dispatch({
      type: CALL_SERVICE,
      payload: {
        service: 'outcomes',
        method: 'getOutcomeResults',
        args: [host, jwt, artifactType, artifactId, outcomeId, userList]
      }
    });
  };
};
var getOutcomeRollups = function getOutcomeRollups(artifactType, artifactId) {
  return function (dispatch, getState, _arg, scope) {
    var _getConfig3 = getConfig(getState(), scope),
      host = _getConfig3.host,
      jwt = _getConfig3.jwt;
    return dispatch({
      type: CALL_SERVICE,
      payload: {
        service: 'outcomes',
        method: 'getOutcomeRollups',
        args: [host, jwt, artifactType, artifactId]
      }
    });
  };
};

// Functions for loading the Outcome Analysis Report
export var loadPage = function loadPage(artifactType, artifactId, pageNumber, loadUsersOverride) {
  return function (dispatch, getState, _arg, scope) {
    var hasSeenPage = getHighestPageSeen(getState(), scope) >= pageNumber;
    var loading = getPageLoading(getState(), scope);
    if (!loading) {
      if (hasSeenPage) {
        return Promise.resolve(dispatch(setPage({
          number: pageNumber,
          loading: false
        })));
      } else {
        dispatch(setLoading(true));
        dispatch(setPage({
          number: pageNumber,
          loading: true
        }));
        var usersPromiseChain = getUsersPromise(dispatch, artifactType, artifactId, pageNumber, loadUsersOverride);
        return usersPromiseChain.then(function (_ref) {
          var users = _ref.users,
            perPage = _ref.perPage,
            total = _ref.total;
          var pageData = {
            perPage: perPage,
            total: total
          };
          var seenUsers = getUsers(getState(), scope, ALL_USERS);
          var allUsers = _objectSpread(_defineProperty({}, pageNumber, users), seenUsers);
          dispatch(setUsers(allUsers));
          return dispatch(setPageData(pageData));
        }).then(function () {
          return dispatch(loadRollups(artifactType, artifactId));
        }).then(function () {
          return dispatch(setPage({
            number: pageNumber,
            loading: false
          }));
        }).then(function () {
          return dispatch(setLoading(false));
        }).catch(function (e) {
          return dispatch(setError(e));
        });
      }
    } else {
      return Promise.resolve();
    }
  };
};
export var loadRollups = function loadRollups(artifactType, artifactId) {
  return function (dispatch, getState, _arg, scope) {
    var pageNumber = getPageNumber(getState(), scope);
    var userList = getUserList(getState(), scope, pageNumber);
    var rollupsPromise = dispatch(getOutcomeRollups(artifactType, artifactId));
    return rollupsPromise.then(function (json) {
      var outcomes = json.reduce(function (acc, el) {
        return Object.assign(acc, _defineProperty({}, el.outcome.id, el.outcome));
      }, {});
      var rollups = json.map(function (el) {
        return {
          outcomeId: el.outcome.id,
          averageScore: el.average_score,
          count: el.count,
          masteryCount: el.mastery_count,
          childArtifactCount: el.child_artifact_count,
          usesBank: el.uses_bank
        };
      });
      dispatch(setReportOutcomes(outcomes));
      dispatch(setRollups(rollups));
      var resultPromises = json.map(function (el) {
        var resultPromise = dispatch(getOutcomeResults(artifactType, artifactId, el.outcome.id, userList));
        return resultPromise.then(function (results) {
          var seenResults = getResults(getState(), scope, el.outcome.id);
          dispatch(setResults({
            outcomeId: el.outcome.id,
            results: results,
            seenResults: seenResults
          }));
          return results;
        }).catch(function (e) {
          return dispatch(setError(e));
        });
      });
      return Promise.all(resultPromises);
    }).catch(function (e) {
      return dispatch(setError(e));
    });
  };
};

// Functions for fetching the remaining data for the Outcome Analysis Report CSV Download
export var loadRemainingPages = function loadRemainingPages(artifactType, artifactId, loadUsersOverride) {
  return function (dispatch, getState, _arg, scope) {
    var loadingRemainingPages = getLoadingRemainingPages(getState(), scope);
    if (loadingRemainingPages !== IN_PROGRESS) {
      // If there was an error in the last fetch, then the only data that is guaranteed to be
      // loaded is up to the page the user is currently on
      var startPage = loadingRemainingPages === ERROR ? getPageNumber(getState(), scope) : getHighestPageSeen(getState(), scope) + 1;
      var endPage = getPageCount(getState(), scope);
      dispatch(setLoadingRemainingPages(IN_PROGRESS));
      return dispatch(loadRemainingUsers(artifactType, artifactId, startPage, endPage, loadUsersOverride)).then(function () {
        return dispatch(loadRemainingResults(artifactType, artifactId, startPage, endPage));
      }).catch(function (e) {
        dispatch(setError(e));
        dispatch(setLoadingRemainingPages(ERROR));
      });
    } else {
      return Promise.resolve();
    }
  };
};
export var loadRemainingUsers = function loadRemainingUsers(artifactType, artifactId, startPage, endPage, loadUsersOverride) {
  return function (dispatch, getState, _arg, scope) {
    var userPromises = [];
    var _loop = function _loop(pageNumber) {
      var usersPromise = getUsersPromise(dispatch, artifactType, artifactId, pageNumber, loadUsersOverride);
      var usersPromiseChain = usersPromise.then(function (_ref2) {
        var users = _ref2.users,
          perPage = _ref2.perPage,
          total = _ref2.total;
        var pageData = {
          perPage: perPage,
          total: total
        };
        var seenUsers = getUsers(getState(), scope, ALL_USERS);
        var allUsers = _objectSpread(_defineProperty({}, pageNumber, users), seenUsers);
        dispatch(setUsers(allUsers));
        dispatch(setPageData(pageData));
      });
      userPromises.push(usersPromiseChain);
    };
    for (var pageNumber = startPage; pageNumber <= endPage; pageNumber++) {
      _loop(pageNumber);
    }
    return Promise.all(userPromises).catch(function (e) {
      dispatch(setError(e));
      dispatch(setLoadingRemainingPages(ERROR));
    });
  };
};
export var loadRemainingResults = function loadRemainingResults(artifactType, artifactId, startPage, endPage) {
  return function (dispatch, getState, _arg, scope) {
    var rollups = getRollups(getState(), scope);
    var resultPromises = [];
    rollups.forEach(function (rollup) {
      var outcomeId = rollup.outcomeId;
      var paginatedUserList = getPaginatedUserList(getState(), scope, startPage, endPage);
      paginatedUserList.forEach(function (userList) {
        var resultPromise = dispatch(getOutcomeResults(artifactType, artifactId, outcomeId, userList)).then(function (results) {
          var seenResults = getResults(getState(), scope, outcomeId);
          dispatch(setResults({
            outcomeId: outcomeId,
            results: results,
            seenResults: seenResults
          }));
        });
        resultPromises.push(resultPromise);
      });
    });
    return Promise.all(resultPromises).then(function () {
      return dispatch(setLoadingRemainingPages(COMPLETED));
    }).catch(function (e) {
      dispatch(setError(e));
      dispatch(setLoadingRemainingPages(ERROR));
    });
  };
};
export var clearReportStore = function clearReportStore() {
  return function (dispatch, _getState, _arg, _scope) {
    dispatch(clearReportData());
  };
};

// Helpers
var getUsersPromise = function getUsersPromise(dispatch, artifactType, artifactId, pageNumber, loadUsersOverride) {
  // if the override function is provided, we assume the upstream application has handled dispatching and is
  // returning a promise to us
  return loadUsersOverride ? loadUsersOverride(artifactType, artifactId, pageNumber) : dispatch(loadUsers(artifactType, artifactId, pageNumber));
};
export var getUserList = function getUserList(state, scope, page) {
  return getUsers(state, scope, page).map(function (user) {
    return user.uuid;
  });
};
export var getPaginatedUserList = function getPaginatedUserList(state, scope, startPage, endPage) {
  var userList = [];
  for (var i = startPage; i <= endPage; i++) {
    var users = getUserList(state, scope, i);
    userList.push(users);
  }
  return userList;
};